// The roles in words (spec/dashboard.md section 12.4): every role select of the members pages, the Add dialogs and the
// invite dialog shows what the chosen role can do, and "What can each role do?" lists them all. The sentences follow the
// deny-by-default matrix of server/src/Huelency.Domain/Access/RoleMatrix.cs (game permissions, environment permissions by
// environment kind) plus the endpoint rules around it: a build needs content_write, a pin needs deploy on that environment,
// a merge into a protected branch needs deploy on production, only owners add, invite or remove owners.

export interface RoleInfo {
  key: string;
  label: string;
  description: string;
}

export const GAME_ROLES: RoleInfo[] = [
  {
    key: 'admin',
    label: 'Admin',
    description: 'Admin: everything in this game, production included: content and branches, builds and pins on every environment, players, analytics, settings, members, keys and the audit.',
  },
  {
    key: 'developer',
    label: 'Developer',
    description: 'Developer: edits content and LiveOps, creates and deletes branches, builds releases, pins dev and stage, edits the dev environment and dev players, reads players, analytics and the audit; never pins production.',
  },
  {
    key: 'designer',
    label: 'Designer',
    description: 'Designer: edits content and LiveOps, creates branches, builds releases, pins dev and stage, reads players and analytics; never production.',
  },
  {
    key: 'qa',
    label: 'QA',
    description: 'QA: edits content, creates branches, builds releases, pins dev and stage, edits players on dev and stage, reads players, analytics and the audit; never production.',
  },
  {
    key: 'release',
    label: 'Release',
    description: 'Release: reads content, creates branches, pins releases on every environment including production (and merges into protected branches), reads analytics and the audit; never edits content.',
  },
  {
    key: 'support',
    label: 'Support',
    description: 'Support: reads content, reads and edits players on every environment including production; no content edits, pins or analytics.',
  },
  {
    key: 'viewer',
    label: 'Viewer',
    description: 'Viewer: reads content only; no edits, pins, players or analytics.',
  },
];

export const ORG_ROLES: RoleInfo[] = [
  {
    key: 'owner',
    label: 'Owner',
    description: 'Owner: every permission in the organization and in all its games, production included; the only role that adds, invites or removes owners.',
  },
  {
    key: 'admin',
    label: 'Admin',
    description: 'Admin: every permission in the organization and in all its games, production included (members, keys, integrations, audit), except managing owners.',
  },
  {
    key: 'member',
    label: 'Member',
    description: 'Member: sees the organization and its games; what they can do in a game comes from their game role.',
  },
];

export type RoleKind = 'game' | 'org';

export function rolesOf(kind: RoleKind): RoleInfo[] {
  return kind === 'game' ? GAME_ROLES : ORG_ROLES;
}

/** The role's entry, or a bare one for a role this build does not know. */
export function roleInfo(kind: RoleKind, key: string | null | undefined): RoleInfo | undefined {
  if (!key) return undefined;
  return rolesOf(kind).find((r) => r.key === key);
}

export function roleLabel(kind: RoleKind, key: string | null | undefined): string {
  return roleInfo(kind, key)?.label ?? key ?? '';
}

export function roleDescription(kind: RoleKind, key: string | null | undefined): string | undefined {
  return roleInfo(kind, key)?.description;
}
